import * as React from 'react';

import {
  PublicationApiError,
  type PreviewBandDto,
  type PublicationStateDto,
  type SnapshotPublicationStateDto,
} from '@/api/publication-client';
import { useCampaignDetail } from '@/api/use-campaigns';
import {
  usePublicationPreview,
  usePublish,
  useUnpublish,
  useValidateAnalysis,
} from '@/api/use-publication';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Modal } from '@/components/ui/modal';
import { Textarea } from '@/components/ui/textarea';
import { catalogVersionLabel } from '@/lib/catalog-version-label';
import { formatEur, seniorityLabel } from '@/lib/operational-view-labels';
import {
  formatDateTime,
  formatOpaqueId,
} from '@/routes/admin/cgu/cgu-format';

// =============================================================================
// publication-screen.tsx — Écran ADMIN « Publication du benchmark vers Pulse » (S18 / stream C).
// =============================================================================
// Consomme les surfaces back stream C via le PublicationClient (cookie admin + admin_csrf). L'écran LIT et
// HABILLE ; AUCUNE règle métier (anonymisation, transitions Disponible→Validee→Publiee, irréversibilité,
// exigence du double-confirm sont l'autorité de l'API/worker).
//
// DÉCISIONS GATE_HUMAN appliquées :
//   - Cx-S18-5 (aperçu FIDÈLE) : l'aperçu est le SNAPSHOT ANONYMISÉ — bands {profil, niveau, P25, P75,
//     suppressed}. AUCUN champ nominatif n'est lu ni affiché (le type PreviewSnapshotDto ne déclare ni siret,
//     ni effectif, ni submissionCount, ni min/max : interdiction STRUCTURELLE côté front).
//   - PP-08 (friction FORTEMENT ENGAGEANTE + journalisée) : modale FRICTION MAX + DOUBLE CONFIRMER (2
//     confirmations textuelles explicites : case d'acquittement + saisie « CONFIRMER ») AVANT d'appeler
//     publish({confirm:true, confirmAgain:true}). Le bouton « Publier » est DÉSACTIVÉ hors état Validée
//     (l'admin doit d'abord valider Disponible→Validée). 409 (double-confirm manquant / déjà publié / mauvais
//     état) affiché tel quel (message API).
//   - V26-PUBLICATION-FINISH (DBU §2.6 l.1442, l.1448) : la copy N'AFFIRME PLUS l'irréversibilité — la
//     visibilité PEUT être RÉ-ÉVALUÉE par l'administrateur (dé-publication manuelle DBA + audit en V1, DBU
//     §2.6 l.1450, hors périmètre self-service). La FRICTION PP-08 (double-confirm) est CONSERVÉE : le geste
//     reste fortement engageant et journalisé même si la copy n'annonce plus une bascule sans retour possible.
//     Badge post-publication (l.1448) : date/heure + identité admin CONSERVÉES, re-lisibles après reload
//     (`publishedAt`/`publishedBy` du snapshot d'aperçu, thread de lecture ajouté par ce lot).
//
// WCAG 2.1 AA : <table>/<caption>/<th scope>, état en LIBELLÉ texte + badge (jamais la couleur seule), bandes
// supprimées doublées d'un libellé « Hors-volume » (jamais une couleur seule), modale role=dialog + focus-trap
// + aria, formulaires <label> liés, messages role="status"/role="alert". jest-axe 0.

/** Habillage de l'état de publication en libellé + badge (jamais la couleur seule). */
const PUBLICATION_STATE_PRESENTATION: Record<
  PublicationStateDto,
  { label: string; badgeClassName: string }
> = {
  // DS sweep Cat 2 : palette Tailwind brute retirée au profit de tokens DS. Recette
  // AA-SAFE (parité status badge) : fond = tint DS, texte = `text-foreground` (jamais
  // le tone sur le texte), tone via la bordure. Le LIBELLÉ porte l'information (WCAG
  // 1.4.1) ; la teinte n'est qu'un renfort. Tokens choisis hue-fidèles aux couleurs
  // d'origine (amber→chart-3, purple→chart-4).
  EnAttente: {
    label: 'En attente',
    badgeClassName:
      'border-status-inactive/40 bg-status-inactive-bg text-foreground',
  },
  EnTraitement: {
    label: 'En traitement',
    badgeClassName: 'border-chart-3/40 bg-chart-3/10 text-foreground',
  },
  Disponible: {
    label: 'Disponible',
    badgeClassName:
      'border-status-invited/40 bg-status-invited-bg text-foreground',
  },
  Validee: {
    label: 'Validée',
    badgeClassName:
      'border-status-active/40 bg-status-active-bg text-foreground',
  },
  Publiee: {
    label: 'Publiée',
    badgeClassName: 'border-chart-4/40 bg-chart-4/10 text-foreground',
  },
};

/**
 * Habillage de la VISIBILITÉ PULSE (publicationState : Draft/Published) en libellé + badge. Distinct du cycle
 * de vie (analysisState) : ce badge porte le fait DE DIFFUSION RÉEL vers Pulse. Le LIBELLÉ porte l'information
 * (WCAG 1.4.1) ; la teinte n'est qu'un renfort (jamais la couleur seule). Recette AA-SAFE (parité status badge).
 */
const PULSE_VISIBILITY_PRESENTATION: Record<
  SnapshotPublicationStateDto,
  { label: string; badgeClassName: string }
> = {
  Published: {
    label: 'Visible sur Pulse',
    badgeClassName:
      'border-status-active/40 bg-status-active-bg text-foreground',
  },
  Draft: {
    label: 'Non visible sur Pulse',
    badgeClassName:
      'border-status-inactive/40 bg-status-inactive-bg text-foreground',
  },
};

/**
 * Compose un message d'erreur ACTIONNABLE pour la dé-publication (UX-03). L'UI ne réinterprète PAS la règle
 * métier : elle traduit un CODE de cas (server-set) en une consigne utilisateur claire. Repli sur le message
 * serveur tel quel quand le code est inconnu (aucune règle inventée côté front).
 *   400 ANALYSIS_UNPUBLISH_MOTIF_REQUIRED → saisir un motif (garde de bord : le bouton est déjà désactivé) ;
 *   409 ANALYSIS_NOT_PUBLISHED           → rien à dé-publier (recharger pour l'état à jour) ;
 *   409 ANALYSIS_UNPUBLISH_CONFLICT      → conflit concurrent (recharger puis réessayer) ;
 *   404 (aucune analyse)                 → aucune analyse pour cette campagne.
 */
function unpublishErrorMessage(error: Error): string {
  if (error instanceof PublicationApiError) {
    switch (error.code) {
      case 'ANALYSIS_UNPUBLISH_MOTIF_REQUIRED':
        return 'Le motif est obligatoire pour dé-publier le benchmark.';
      case 'ANALYSIS_NOT_PUBLISHED':
        return 'Ce benchmark n’est pas (ou plus) publié : il n’y a rien à dé-publier. Rechargez la page pour voir l’état à jour.';
      case 'ANALYSIS_UNPUBLISH_CONFLICT':
        return 'La dé-publication est entrée en conflit avec une autre opération en cours. Rechargez la page, puis réessayez.';
      default:
        break;
    }
    if (error.status === 404) {
      return 'Aucune analyse à dé-publier n’a été trouvée pour cette campagne.';
    }
  }
  return error.message;
}

export interface PublicationScreenProps {
  readonly campaignId: string;
}

export function PublicationScreen({
  campaignId,
}: PublicationScreenProps): React.JSX.Element {
  const previewQuery = usePublicationPreview(campaignId);
  // P-8 : le libellé de la version de catalogue VIT côté campagne (port ACL campaign → catalog). Le
  // snapshot d'analyse, lui, ne porte que l'uuid. On relit donc la campagne — lecture déjà en cache
  // TanStack Query (la zone campagne l'a résolue pour sa barre de navigation), aucun appel réseau ajouté.
  const campaignQuery = useCampaignDetail(campaignId);
  const validate = useValidateAnalysis(campaignId);
  const publish = usePublish(campaignId);
  const unpublish = useUnpublish(campaignId);

  const [publishOpen, setPublishOpen] = React.useState(false);
  const [unpublishOpen, setUnpublishOpen] = React.useState(false);

  const snapshot = previewQuery.data;
  // DEUX états DISTINCTS, gouvernant des choses DIFFÉRENTES (réconciliation DEC-39) :
  //   - `analysisState` (Disponible→Validee→Publiee) = CYCLE DE VIE de l'analyse. Gouverne les actions de
  //     VALIDATION / PREMIÈRE PUBLICATION. Le backend ne le reverte PAS à la dé-publication (DEC-39,
  //     volontaire) : après dé-publication il reste `Publiee`.
  //   - `publicationState` (Draft/Published) = VISIBILITÉ PULSE RÉELLE (flag de diffusion). Gouverne le badge
  //     « Publié », la visibilité du bouton « Dé-publier » et le statut « non visible sur Pulse ». La
  //     dé-publication flippe SEULEMENT ce flag Published→Draft.
  const state = snapshot?.analysisState;
  const statePresentation =
    state !== undefined ? PUBLICATION_STATE_PRESENTATION[state] : undefined;
  const pulseState = snapshot?.publicationState;
  const pulsePresentation =
    pulseState !== undefined ? PULSE_VISIBILITY_PRESENTATION[pulseState] : undefined;

  // P-8 — nommer la version de catalogue du snapshot. L'uuid affiché ici vient du SNAPSHOT, pas de la
  // campagne : on ne substitue le NUMÉRO que si les deux désignent bien la même version. Un écart (calcul
  // antérieur à un re-figement, snapshot importé) doit rester VISIBLE sous forme d'uuid — le maquiller
  // derrière un numéro emprunté à la campagne afficherait une version que ce snapshot n'a jamais utilisée.
  const snapshotCatalogVersionId = snapshot?.catalogVersionId;
  const campaignCatalogVersionNumber =
    snapshotCatalogVersionId !== undefined &&
    snapshotCatalogVersionId === campaignQuery.data?.catalogVersionId
      ? campaignQuery.data.catalogVersionNumber
      : undefined;
  const catalogVersion =
    snapshotCatalogVersionId === undefined
      ? { text: '—', isFallback: true }
      : catalogVersionLabel(snapshotCatalogVersionId, campaignCatalogVersionNumber);

  // PP-08 : « Valider » actif SEULEMENT en Disponible ; « Publier » (première publication) actif SEULEMENT en
  // Validée. Ces deux gestes sont gouvernés par le CYCLE DE VIE (analysisState), inchangé par la dé-publication.
  const canValidate = state === 'Disponible';
  const canPublish = state === 'Validee';
  // Le CYCLE DE VIE a déjà atteint « Publiée » (première publication faite). Après dé-publication il reste vrai
  // (DEC-39) : la re-publication après correction est un flux SÉPARÉ, hors périmètre UX-03 → on N'AFFICHE PLUS
  // le bouton « Publier » une fois ce cap franchi.
  const lifecyclePublished = state === 'Publiee';
  // VISIBILITÉ PULSE RÉELLE : gouverne le badge « Publié », le bouton « Dé-publier » et le statut de retrait.
  const isVisibleOnPulse = pulseState === 'Published';
  // Dé-publiée : le cycle de vie a été publié une fois, mais la visibilité Pulse a été retirée (Draft).
  const isUnpublished = lifecyclePublished && !isVisibleOnPulse;

  const onConfirmPublish = React.useCallback((): void => {
    // La double confirmation textuelle a déjà été franchie dans la modale : on relaie les 2 drapeaux.
    publish.mutate(
      { confirm: true, confirmAgain: true },
      { onSuccess: () => setPublishOpen(false) },
    );
  }, [publish]);

  const onConfirmUnpublish = React.useCallback(
    (motif: string): void => {
      // Le motif (obligatoire, non vide) porte l'intentionnalité ; la modale a déjà gardé le bouton désactivé
      // tant qu'il est vide. À la réussite, on ferme la modale ; l'aperçu est ré-invalidé (état repasse Draft).
      unpublish.mutate(
        { motif },
        { onSuccess: () => setUnpublishOpen(false) },
      );
    },
    [unpublish],
  );

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Publication du benchmark
          </h1>
          <p className="text-sm text-muted-foreground">
            Validez l’analyse consolidée, vérifiez l’aperçu anonymisé, puis
            publiez le benchmark vers Pulse. La visibilité peut être
            ré-évaluée par l’administrateur par la suite, mais ce geste reste
            fortement engageant et journalisé.
          </p>
        </div>
      </header>

      {/* (META + ACTIONS) État de publication + validation + déclencheur de publication. */}
      <Card>
        <CardHeader>
          <CardTitle>État de l’analyse</CardTitle>
          <CardDescription>
            L’analyse doit être « Validée » avant de pouvoir être publiée. La
            publication bascule l’état en « Publiée » et diffuse le benchmark
            anonymisé vers Pulse.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1">
              <dt className="text-sm text-muted-foreground">
                Cycle de vie de l’analyse
              </dt>
              <dd>
                {statePresentation !== undefined ? (
                  <Badge
                    variant="outline"
                    className={statePresentation.badgeClassName}
                  >
                    {statePresentation.label}
                  </Badge>
                ) : (
                  <span className="text-sm text-muted-foreground" role="status">
                    Chargement…
                  </span>
                )}
              </dd>
            </div>
            {/* VISIBILITÉ PULSE RÉELLE (publicationState) : distincte du cycle de vie. Après dé-publication, le
                cycle reste « Publiée » mais la visibilité repasse « Non visible sur Pulse » (DEC-39). */}
            <div className="space-y-1">
              <dt className="text-sm text-muted-foreground">Visibilité Pulse</dt>
              <dd>
                {pulsePresentation !== undefined ? (
                  <Badge
                    variant="outline"
                    className={pulsePresentation.badgeClassName}
                  >
                    {pulsePresentation.label}
                  </Badge>
                ) : (
                  <span className="text-sm text-muted-foreground" role="status">
                    Chargement…
                  </span>
                )}
              </dd>
            </div>
            <div className="space-y-1">
              <dt className="text-sm text-muted-foreground">
                Version de catalogue
              </dt>
              {/* Le repli est TRONQUÉ à 8 caractères (parité des autres surfaces) : l'uuid complet
                  reste accessible en infobulle, et c'est lui qu'on transmet au support. */}
              <dd
                className={catalogVersion.isFallback ? 'font-mono text-sm' : 'text-sm'}
                title={snapshotCatalogVersionId}
              >
                {catalogVersion.text}
              </dd>
            </div>
          </dl>

          <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
            {/* Étape 1 : valider (Disponible → Validée). Actif SEULEMENT en Disponible. */}
            <Button
              type="button"
              variant="outline"
              onClick={() => validate.mutate()}
              disabled={!canValidate || validate.isPending}
            >
              {validate.isPending
                ? 'Validation…'
                : 'Valider le benchmark'}
            </Button>

            {/* Étape 2 : PREMIÈRE publication (Validée → Publiée). Gouvernée par le CYCLE DE VIE (analysisState).
                RETIRÉE une fois le cycle « Publiée » atteint : la re-publication après dé-publication est un
                flux SÉPARÉ, hors périmètre UX-03 (DEC-39). */}
            {!lifecyclePublished ? (
              <Button
                type="button"
                onClick={() => setPublishOpen(true)}
                disabled={!canPublish || publish.isPending}
              >
                {publish.isPending ? 'Publication…' : 'Publier vers Pulse'}
              </Button>
            ) : null}

            {/* (UX-03, DBU §2.6 l.1450) DÉ-PUBLIER — visible UNIQUEMENT quand le benchmark est VISIBLE sur Pulse
                (publicationState = Published). Flippe la visibilité Published→Draft (retrait de Pulse) en cas
                d'anomalie découverte post-publication. Geste engageant et journalisé (l.1442) : friction =
                modale + motif obligatoire. */}
            {isVisibleOnPulse ? (
              <Button
                type="button"
                variant="destructive"
                onClick={() => setUnpublishOpen(true)}
                disabled={unpublish.isPending}
              >
                {unpublish.isPending ? 'Dé-publication…' : 'Dé-publier'}
              </Button>
            ) : null}

            {/* Aide CONTEXTUELLE en libellé texte (jamais une couleur seule). */}
            {!canPublish && !lifecyclePublished ? (
              <p className="text-sm text-muted-foreground" role="status">
                Le bouton « Publier » est désactivé : validez d’abord le
                benchmark (état « Validée » requis).
              </p>
            ) : null}
            {isVisibleOnPulse ? (
              <p className="text-sm text-muted-foreground" role="status">
                Le benchmark est publié et visible sur le portail Pulse. La
                visibilité peut être ré-évaluée par l’administrateur
                (dé-publication possible en cas de découverte d’anomalie
                post-publication).
              </p>
            ) : null}
            {isUnpublished ? (
              <p className="text-sm text-muted-foreground" role="status">
                Analyse dé-publiée : le benchmark n’est plus visible sur le
                portail Pulse. La re-publication après correction relève d’un
                flux séparé.
              </p>
            ) : null}
          </div>

          {/* (BADGE POST-PUBLICATION, DBU §2.6 l.1448) date/heure + identité admin, gouverné par la VISIBILITÉ
              PULSE RÉELLE (Published). Après dé-publication (Draft), le badge « Publiée le » est RETIRÉ. */}
          {isVisibleOnPulse &&
          snapshot?.publishedAt !== null &&
          snapshot?.publishedAt !== undefined ? (
            <dl className="border-t border-border pt-4">
              <div className="space-y-1">
                <dt className="text-sm text-muted-foreground">
                  Publiée le
                </dt>
                <dd
                  className="text-sm"
                  aria-label={
                    snapshot.publishedBy === null
                      ? `Publiée le ${formatDateTime(snapshot.publishedAt)}, administrateur non disponible`
                      : `Publiée le ${formatDateTime(snapshot.publishedAt)}, par l’administrateur identifiant ${snapshot.publishedBy}`
                  }
                >
                  {formatDateTime(snapshot.publishedAt)}
                  {' · '}
                  <span className="font-mono text-muted-foreground">
                    {formatOpaqueId(snapshot.publishedBy)}
                  </span>
                </dd>
              </div>
            </dl>
          ) : null}

          {validate.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {validate.error.message}
            </p>
          ) : null}
          {publish.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {publish.error.message}
            </p>
          ) : null}
          {publish.isSuccess ? (
            <p role="status" className="text-sm text-muted-foreground">
              Benchmark publié vers Pulse.
            </p>
          ) : null}
          {unpublish.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {unpublishErrorMessage(unpublish.error)}
            </p>
          ) : null}
          {unpublish.isSuccess ? (
            <p role="status" className="text-sm text-muted-foreground">
              Benchmark dé-publié : il n’est plus visible sur le portail Pulse.
              La dé-publication est journalisée.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {/* (APERÇU FIDÈLE) Snapshot ANONYMISÉ : bandes p25/p75/suppressed — décision Cx-S18-5. */}
      <PreviewCard
        bands={snapshot?.bands}
        kAnonymityThreshold={snapshot?.kAnonymityThreshold}
        isPending={previewQuery.isPending}
        isError={previewQuery.isError}
        errorMessage={previewQuery.error?.message}
      />

      {/* (PP-08) Modale FRICTION MAX + DOUBLE CONFIRMER avant publish({confirm, confirmAgain}). */}
      <PublishConfirmDialog
        open={publishOpen}
        pending={publish.isPending}
        onConfirm={onConfirmPublish}
        onCancel={() => setPublishOpen(false)}
      />

      {/* (UX-03, DBU §2.6 l.1450) Modale de dé-publication : motif OBLIGATOIRE, pas de double-confirm lourd
          (l'action est réversible ; l'intentionnalité repose sur le motif + la confirmation). */}
      <UnpublishConfirmDialog
        open={unpublishOpen}
        pending={unpublish.isPending}
        onConfirm={onConfirmUnpublish}
        onCancel={() => setUnpublishOpen(false)}
      />
    </div>
  );
}

// --- (APERÇU FIDÈLE) Tableau des bandes anonymisées (p25/p75/suppressed) ------

interface PreviewCardProps {
  readonly bands: readonly PreviewBandDto[] | undefined;
  readonly kAnonymityThreshold: number | undefined;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly errorMessage: string | undefined;
}

function PreviewCard({
  bands,
  kAnonymityThreshold,
  isPending,
  isError,
  errorMessage,
}: PreviewCardProps): React.JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Aperçu anonymisé (avant publication)</CardTitle>
        <CardDescription>
          Aperçu FIDÈLE de ce qui sera diffusé vers Pulse : bandes P25 / P75 par
          profil × séniorité.{' '}
          {kAnonymityThreshold !== undefined
            ? `Les couples sous le seuil de confidentialité (K-anonymity ≥ ${String(kAnonymityThreshold)}) sont masqués (« Hors-volume »).`
            : 'Les couples sous le seuil de confidentialité sont masqués (« Hors-volume »).'}{' '}
          Aucune raison sociale, aucun SIRET, aucun effectif, aucune valeur
          individuelle.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <p role="status" className="text-muted-foreground">
            Chargement de l’aperçu…
          </p>
        ) : isError ? (
          <p role="alert" className="text-destructive">
            Erreur de chargement : {errorMessage}
          </p>
        ) : (bands?.length ?? 0) === 0 ? (
          <p role="status" className="text-muted-foreground">
            Aucune bande à publier pour cette campagne.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="ds-table w-full border-collapse text-sm">
              <caption className="sr-only">
                Bandes anonymisées P25 / P75 par profil et séniorité, avec l’état
                de masquage des couples sous le seuil de confidentialité.
              </caption>
              <thead>
                <tr className="border-b border-border">
                  <th
                    scope="col"
                    className="px-3 py-2 text-left font-medium text-muted-foreground"
                  >
                    Profil
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-left font-medium text-muted-foreground"
                  >
                    Séniorité
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-left font-medium text-muted-foreground"
                  >
                    P25
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-left font-medium text-muted-foreground"
                  >
                    P75
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-left font-medium text-muted-foreground"
                  >
                    État
                  </th>
                </tr>
              </thead>
              <tbody>
                {bands?.map((band) => (
                  <tr
                    key={`${band.profileCode}:${band.seniorityLevel}`}
                    className="border-b border-border last:border-0"
                  >
                    <td className="px-3 py-2">{band.profileCode}</td>
                    <td className="px-3 py-2">
                      {seniorityLabel(band.seniorityLevel)}
                    </td>
                    <td className="px-3 py-2 tabular-nums">
                      {formatEur(band.p25)}
                    </td>
                    <td className="px-3 py-2 tabular-nums">
                      {formatEur(band.p75)}
                    </td>
                    <td className="px-3 py-2">
                      {band.suppressed ? (
                        // Doublé d'un LIBELLÉ texte (jamais une couleur seule).
                        <Badge
                          variant="outline"
                          className="border-status-inactive/40 bg-status-inactive-bg text-foreground"
                        >
                          Hors-volume (masqué)
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">Diffusé</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- (PP-08) Modale FRICTION MAX + DOUBLE CONFIRMER --------------------------
// Friction max : un ConfirmDialog simple ne suffit pas (1 clic). On exige DEUX confirmations textuelles
// explicites dans une modale dédiée (focus-trap, role=dialog, aria) :
//   (1) cocher « Je comprends que ce geste est fortement engageant et journalisé » ;
//   (2) saisir le mot « CONFIRMER » à l'identique.
// Le bouton « Publier définitivement » reste DÉSACTIVÉ tant que les DEUX ne sont pas franchies. Ce n'est
// qu'alors que onConfirm est autorisé → publish({confirm:true, confirmAgain:true}).
// V26-PUBLICATION-FINISH (DBU §2.6 l.1442) : la copy N'AFFIRME PLUS l'irréversibilité (la visibilité peut
// être RÉ-ÉVALUÉE par l'admin, dé-publication DBA manuelle possible en V1). La FRICTION (double confirmation)
// est CONSERVÉE À L'IDENTIQUE : seule l'affirmation d'irréversibilité change, pas le geste requis.

const CONFIRM_WORD = 'CONFIRMER';

interface PublishConfirmDialogProps {
  readonly open: boolean;
  readonly pending: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

function PublishConfirmDialog({
  open,
  pending,
  onConfirm,
  onCancel,
}: PublishConfirmDialogProps): React.JSX.Element | null {
  const [acknowledged, setAcknowledged] = React.useState(false);
  const [typed, setTyped] = React.useState('');
  const checkboxRef = React.useRef<HTMLInputElement | null>(null);
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  const previouslyFocused = React.useRef<HTMLElement | null>(null);

  // Réinitialise les 2 confirmations à chaque ouverture (jamais d'état « collant » entre ouvertures).
  React.useEffect(() => {
    if (!open) {
      return;
    }
    setAcknowledged(false);
    setTyped('');
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    checkboxRef.current?.focus();

    // Focus-trap COMPLET (PP-08, décision Cx-S19-4) : publication irréversible / legally-binding. Le focus
    // ne s'échappe JAMAIS derrière la modale. Tab sur le DERNIER focusable → revient au PREMIER ; Shift-Tab
    // sur le PREMIER → va au DERNIER (cycle confiné). Échap ferme (restitution gérée au démontage de l'effet).
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onCancel();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const dialog = dialogRef.current;
      if (dialog === null) {
        return;
      }
      // Focusables visibles dans la modale, dans l'ordre du DOM. Les contrôles désactivés (ex. « Publier
      // définitivement » tant que les 2 confirmations ne sont pas franchies) sont exclus du cycle.
      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (first === undefined || last === undefined) {
        // Aucun focusable : on confine le focus sur le conteneur de dialog lui-même.
        event.preventDefault();
        dialog.focus();
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey) {
        // Shift-Tab sur le PREMIER (ou hors modale) → va au DERNIER.
        if (active === first || !dialog.contains(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }
      // Tab sur le DERNIER (ou hors modale) → revient au PREMIER.
      if (active === last || !dialog.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused.current?.focus();
    };
  }, [open, onCancel]);

  if (!open) {
    return null;
  }

  // DOUBLE CONFIRMER : les 2 confirmations doivent être franchies pour autoriser la publication.
  const bothConfirmed = acknowledged && typed.trim() === CONFIRM_WORD;
  const titleId = 'publish-dialog-title';
  const descId = 'publish-dialog-description';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-foreground/40"
        aria-hidden="true"
        onClick={onCancel}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        tabIndex={-1}
        className="relative z-10 w-full max-w-lg space-y-5 rounded-lg border border-destructive/40 bg-card p-6 text-card-foreground shadow-lg"
      >
        <div className="space-y-2">
          <h2
            id={titleId}
            className="text-lg font-semibold tracking-tight text-destructive"
          >
            Publier le benchmark vers Pulse ?
          </h2>
          <p id={descId} className="text-sm text-muted-foreground">
            Cette action rendra le benchmark anonymisé visible aux clients via
            le portail Pulse. La visibilité{' '}
            <strong>peut être ré-évaluée par l’administrateur</strong>{' '}
            (dé-publication possible en cas de découverte d’anomalie
            post-publication), mais le geste reste{' '}
            <strong>fortement engageant et journalisé</strong>. Confirmez deux
            fois pour continuer.
          </p>
        </div>

        {/* Confirmation 1 : case à cocher explicite. */}
        <div className="flex items-start gap-3">
          <input
            ref={checkboxRef}
            id="publish-acknowledge"
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-input"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <Label htmlFor="publish-acknowledge" className="font-normal">
            Je comprends que ce geste est fortement engageant et journalisé.
          </Label>
        </div>

        {/* Confirmation 2 : saisie du mot CONFIRMER à l'identique. */}
        <div className="space-y-1.5">
          <Label htmlFor="publish-confirm-word">
            Saisissez « {CONFIRM_WORD} » pour confirmer définitivement.
          </Label>
          <Input
            id="publish-confirm-word"
            type="text"
            value={typed}
            autoComplete="off"
            onChange={(e) => setTyped(e.target.value)}
            placeholder={CONFIRM_WORD}
          />
        </div>

        <div className="flex justify-end gap-3 border-t border-border pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={pending}
          >
            Annuler
          </Button>
          <Button
            type="button"
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={onConfirm}
            // DÉSACTIVÉ tant que les 2 confirmations ne sont pas franchies (DOUBLE CONFIRMER).
            disabled={!bothConfirmed || pending}
          >
            {pending ? 'Publication…' : 'Publier définitivement'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// --- (UX-03, DBU §2.6 l.1450) Modale de DÉ-PUBLICATION -----------------------
// Friction PROPORTIONNÉE : la dé-publication est RÉVERSIBLE (re-bascule Draft, l'admin corrige l'anomalie puis
// re-publie), donc PAS de double-confirm textuel lourd (décision de mandat). L'intentionnalité repose sur le
// MOTIF obligatoire + la confirmation. On réutilise la Modale accessible partagée (modal.tsx) : role=dialog,
// aria-labelledby/describedby, PIÈGE DE FOCUS complet, restitution du focus à l'ouvrant, Échap/overlay ferment.
// Le bouton « Dé-publier » reste DÉSACTIVÉ tant que le motif est vide (garde de bord ; l'API fail-close aussi).
//
// COPY (reformulation du SENS de DBU §2.6 l.1442, SANS em-dash parenthétique, invariant n°6) : la
// dé-publication retire le benchmark de la visibilité Pulse et elle est journalisée.

interface UnpublishConfirmDialogProps {
  readonly open: boolean;
  readonly pending: boolean;
  readonly onConfirm: (motif: string) => void;
  readonly onCancel: () => void;
}

function UnpublishConfirmDialog({
  open,
  pending,
  onConfirm,
  onCancel,
}: UnpublishConfirmDialogProps): React.JSX.Element | null {
  const [motif, setMotif] = React.useState('');

  // Réinitialise le motif à chaque ouverture (jamais d'état « collant » entre deux dé-publications).
  React.useEffect(() => {
    if (open) {
      setMotif('');
    }
  }, [open]);

  const motifEmpty = motif.trim() === '';

  const handleConfirm = React.useCallback((): void => {
    if (motifEmpty || pending) {
      return;
    }
    onConfirm(motif);
  }, [motifEmpty, pending, onConfirm, motif]);

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title="Dé-publier le benchmark de Pulse ?"
      description="La dé-publication retire le benchmark de la visibilité Pulse. Cette action est journalisée. La re-publication après correction de l’anomalie relève d’un flux séparé."
    >
      <div className="space-y-1.5">
        <Label htmlFor="unpublish-motif">
          Motif de la dé-publication (obligatoire)
        </Label>
        <Textarea
          id="unpublish-motif"
          value={motif}
          aria-required="true"
          aria-invalid={motifEmpty}
          autoComplete="off"
          onChange={(e) => setMotif(e.target.value)}
          placeholder="Exemple : anomalie de calcul détectée sur le profil DEV-FULLSTACK, benchmark à recalculer."
        />
        <p className="text-sm text-muted-foreground">
          Le motif est conservé au journal d’audit. La confirmation reste
          désactivée tant que le motif est vide.
        </p>
      </div>

      <div className="flex justify-end gap-3 border-t border-border pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={pending}
        >
          Annuler
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={handleConfirm}
          // DÉSACTIVÉ tant que le motif est vide (intentionnalité) ou qu'une dé-publication est en cours.
          disabled={motifEmpty || pending}
        >
          {pending ? 'Dé-publication…' : 'Confirmer la dé-publication'}
        </Button>
      </div>
    </Modal>
  );
}
